const clean = (value) => String(value || '').trim();
export const MEETING_AUDIO_INPUT_KEY = 'meeting.audioInputId';
export const MEETING_AUDIO_OUTPUT_KEY = 'meeting.audioOutputId';
export const MEETING_MIC_GAIN_KEY = 'meeting.micGain';
export const MEETING_AUDIO_MODE_KEY = 'meeting.audioMode.v1';
export const MEETING_AUDIO_MODES = Object.freeze({
  VOICE: 'voice',
  WORSHIP: 'worship',
});

export const shouldRetryAudioCapture = (error) => ![
  'NotAllowedError', 'PermissionDeniedError', 'SecurityError',
  'NotReadableError', 'STALE_MEDIA_OPERATION',
].some((name) => name === String(error?.code || '') || name === String(error?.name || ''));

export const notifyMeetingInputDeviceChange = ({
  deviceId = '', trackId = '', enabled = false, reason = 'device-selected',
} = {}) => {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent('meeting-input-device-change', {
    detail: { kind: 'audio', deviceId: clean(deviceId), trackId: clean(trackId),
      enabled: Boolean(enabled), reason },
  }));
};

export const normalizeMeetingAudioMode = (value) =>
  value === MEETING_AUDIO_MODES.WORSHIP
    ? MEETING_AUDIO_MODES.WORSHIP
    : MEETING_AUDIO_MODES.VOICE;

export const getStoredMeetingAudioMode = () => {
  if (typeof window === 'undefined') return MEETING_AUDIO_MODES.VOICE;
  try {
    return normalizeMeetingAudioMode(
      localStorage.getItem(MEETING_AUDIO_MODE_KEY)
    );
  } catch {
    return MEETING_AUDIO_MODES.VOICE;
  }
};

export const persistMeetingAudioMode = (value) => {
  const mode = normalizeMeetingAudioMode(value);
  if (typeof window !== 'undefined') {
    try { localStorage.setItem(MEETING_AUDIO_MODE_KEY, mode); } catch {}
  }
  return mode;
};

export const clampMicGain = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed)
    ? Math.max(0.25, Math.min(2, parsed))
    : 1;
};

export const getStoredMicGain = () => {
  if (typeof window === 'undefined') return 1;
  try {
    return clampMicGain(localStorage.getItem(MEETING_MIC_GAIN_KEY) || 1);
  } catch {
    return 1;
  }
};

export const supportsMicrophoneGain = () => {
  if (typeof window === 'undefined') return false;
  const AudioContextClass = window.AudioContext || window.webkitAudioContext;
  if (!AudioContextClass) return false;
  return (
    typeof AudioContextClass.prototype?.createMediaStreamSource === 'function' &&
    typeof AudioContextClass.prototype?.createMediaStreamDestination === 'function'
  );
};

export const supportsAudioOutputSelection = () =>
  typeof HTMLMediaElement !== 'undefined' &&
  typeof HTMLMediaElement.prototype?.setSinkId === 'function';

export const supportsAudioOutputChooser = () =>
  typeof navigator !== 'undefined' &&
  typeof navigator.mediaDevices?.selectAudioOutput === 'function';

const MEETING_MEDIA_DEVICE_KINDS = [
  'audioinput',
  'videoinput',
  'audiooutput',
];

/**
 * Keep browser-provided MediaDeviceInfo objects intact while removing duplicate
 * IDs. Devices with a blank ID are represented by the permanent "System
 * default" option in the controls instead of creating an unusable select item.
 */
export const groupMeetingMediaDevices = (devices = []) => {
  const grouped = {
    audioinput: [],
    videoinput: [],
    audiooutput: [],
  };
  const seen = {
    audioinput: new Set(),
    videoinput: new Set(),
    audiooutput: new Set(),
  };

  for (const device of Array.from(devices || [])) {
    const kind = String(device?.kind || '');
    const deviceId = String(device?.deviceId || '');
    if (!MEETING_MEDIA_DEVICE_KINDS.includes(kind) || !deviceId) continue;
    if (seen[kind].has(deviceId)) continue;
    seen[kind].add(deviceId);
    grouped[kind].push(device);
  }
  return grouped;
};

export const shouldRequestAudioDevicePermission = (
  devices = [],
  hasLiveAudioTrack = false
) =>
  !hasLiveAudioTrack &&
  !Array.from(devices || []).some(
    (device) =>
      device?.kind === 'audioinput' &&
      Boolean(String(device?.label || '').trim())
  );

/**
 * enumerateDevices intentionally exposes only default/anonymous audio devices
 * before microphone permission. From an explicit user action, briefly opening
 * a microphone makes connected Bluetooth/USB inputs and their associated
 * outputs available, then the temporary track is stopped immediately.
 */
export const scanMeetingMediaDevices = async ({
  mediaDevices =
    typeof navigator !== 'undefined' ? navigator.mediaDevices : undefined,
  requestAudioPermission = false,
  hasLiveAudioTrack = false,
} = {}) => {
  if (!mediaDevices?.enumerateDevices) {
    const error = new Error('This browser cannot list media devices.');
    error.code = 'MEDIA_DEVICE_ENUMERATION_UNSUPPORTED';
    throw error;
  }

  let available = await mediaDevices.enumerateDevices();
  let permissionError = null;

  if (
    requestAudioPermission &&
    mediaDevices.getUserMedia &&
    shouldRequestAudioDevicePermission(available, hasLiveAudioTrack)
  ) {
    let probeStream = null;
    try {
      probeStream = await mediaDevices.getUserMedia({
        audio: true,
        video: false,
      });
    } catch (error) {
      permissionError = error;
    } finally {
      probeStream?.getTracks?.().forEach((track) => {
        try { track.stop(); } catch {}
      });
    }
    // Re-enumerate even after a denial. It keeps the system-default choices
    // usable and also catches devices connected while the permission prompt
    // was open.
    available = await mediaDevices.enumerateDevices();
  }

  return {
    devices: groupMeetingMediaDevices(available),
    permissionError,
  };
};

/**
 * Route one media element to a selected speaker. If a Bluetooth/cast device
 * disappears during the meeting, move this element back to the system default
 * before surfacing the original failure so remote audio is never left silent.
 */
export const applyAudioOutputDevice = async (
  element,
  deviceId = '',
  { fallbackToDefault = true } = {}
) => {
  const requested = clean(deviceId);
  if (!element || typeof element.setSinkId !== 'function') {
    if (requested) {
      const error = new Error(
        'This browser controls speaker selection through the operating system.'
      );
      error.code = 'AUDIO_OUTPUT_UNSUPPORTED';
      throw error;
    }
    return { supported: false, deviceId: '' };
  }

  try {
    await element.setSinkId(requested);
    return { supported: true, deviceId: requested };
  } catch (cause) {
    if (requested && fallbackToDefault) {
      try {
        await element.setSinkId('');
      } catch {
        // Preserve the original device error; the OS still owns the fallback.
      }
    }
    const error = new Error(
      requested
        ? 'The selected speaker is unavailable. Audio has returned to the system default.'
        : 'The system speaker is unavailable.'
    );
    error.code = cause?.name || 'AUDIO_OUTPUT_FAILED';
    error.cause = cause;
    throw error;
  }
};

/**
 * Validate output selection from the user's select gesture before persisting
 * it. This catches permission/removed-device failures even when no participant
 * audio element has mounted yet.
 */
export const verifyAudioOutputDevice = async (deviceId = '') => {
  if (typeof document === 'undefined') {
    return { supported: false, deviceId: '' };
  }
  const probe = document.createElement('audio');
  return applyAudioOutputDevice(probe, deviceId, {
    fallbackToDefault: false,
  });
};

/**
 * Speech-first capture settings. Use the browser's standard WebRTC voice DSP
 * once, before Opus encoding. Stacking legacy/experimental Chromium filters
 * can make quiet voices sound thin and can add pumping noise on mobile.
 */
export const createVoiceAudioConstraints = ({ deviceId = '', exact = false } = {}) => ({
  ...(clean(deviceId)
    ? { deviceId: exact ? { exact: clean(deviceId) } : { ideal: clean(deviceId) } }
    : {}),
  // Boolean `true` asks the capture device for the browser's WebRTC speech
  // processing. This is preferable to vendor-prefixed filters, which can be
  // stacked twice and cause pumping/robotic speech.
  echoCancellation: true,
  noiseSuppression: true,
  autoGainControl: true,
  channelCount: { ideal: 1, max: 1 },
  sampleRate: { ideal: 48000 },
  sampleSize: { ideal: 16 },
  latency: { ideal: 0.02 },
});

/**
 * Music-first capture deliberately disables speech DSP. A headset is strongly
 * recommended: without echo cancellation, loudspeaker sound can be captured
 * again by the microphone and returned to the meeting.
 */
export const createWorshipAudioConstraints = ({ deviceId = '', exact = false } = {}) => ({
  ...(clean(deviceId)
    ? { deviceId: exact ? { exact: clean(deviceId) } : { ideal: clean(deviceId) } }
    : {}),
  echoCancellation: false,
  noiseSuppression: false,
  autoGainControl: false,
  channelCount: { ideal: 2 },
  sampleRate: { ideal: 48000 },
  sampleSize: { ideal: 24 },
  latency: { ideal: 0.01 },
});

export const createMeetingAudioConstraints = ({
  mode = MEETING_AUDIO_MODES.VOICE,
  deviceId = '',
  exact = false,
} = {}) =>
  normalizeMeetingAudioMode(mode) === MEETING_AUDIO_MODES.WORSHIP
    ? createWorshipAudioConstraints({ deviceId, exact })
    : createVoiceAudioConstraints({ deviceId, exact });

export const getVoiceAudioConstraintAttempts = (deviceId = '', exact = false) => {
  const selected = clean(deviceId);
  const selectedDevice = selected
    ? { deviceId: { exact: selected } }
    : {};
  const selectedAttempts = [
    createVoiceAudioConstraints({
      deviceId: selected,
      exact: Boolean(selected),
    }),
    {
      ...selectedDevice,
      echoCancellation: true,
      noiseSuppression: true,
      autoGainControl: true,
      channelCount: 1,
    },
    {
      ...selectedDevice,
      echoCancellation: true,
      noiseSuppression: true,
      autoGainControl: true,
    },
    selected ? selectedDevice : true,
  ];
  if (!selected || exact) return selectedAttempts;

  // Try a persisted USB/Bluetooth choice exactly before falling back. `ideal`
  // can silently open the system microphone even while the requested headset
  // is present, making the UI claim one device while publishing another.
  // Explicit live switching passes exact=true and therefore never falls back.
  return [
    ...selectedAttempts,
    createVoiceAudioConstraints(),
    {
      echoCancellation: true,
      noiseSuppression: true,
      autoGainControl: true,
      channelCount: 1,
    },
    {
      echoCancellation: true,
      noiseSuppression: true,
      autoGainControl: true,
    },
    true,
  ];
};

export const getWorshipAudioConstraintAttempts = (deviceId = '', exact = false) => {
  const selected = clean(deviceId);
  const selectedDevice = selected
    ? { deviceId: { exact: selected } }
    : {};
  const selectedAttempts = [
    createWorshipAudioConstraints({
      deviceId: selected,
      exact: Boolean(selected),
    }),
    {
      ...selectedDevice,
      echoCancellation: false,
      noiseSuppression: false,
      autoGainControl: false,
      channelCount: 2,
    },
    {
      ...selectedDevice,
      echoCancellation: false,
      noiseSuppression: false,
      autoGainControl: false,
    },
    selected ? selectedDevice : true,
  ];
  if (!selected || exact) return selectedAttempts;
  return [
    ...selectedAttempts,
    createWorshipAudioConstraints(),
    {
      echoCancellation: false,
      noiseSuppression: false,
      autoGainControl: false,
    },
    true,
  ];
};

export const getMeetingAudioConstraintAttempts = (
  mode = MEETING_AUDIO_MODES.VOICE,
  deviceId = '',
  exact = false
) =>
  normalizeMeetingAudioMode(mode) === MEETING_AUDIO_MODES.WORSHIP
    ? getWorshipAudioConstraintAttempts(deviceId, exact)
    : getVoiceAudioConstraintAttempts(deviceId, exact);

export const prepareVoiceTrack = async (track) => {
  if (!track) return track;
  try { track.contentHint = 'speech'; } catch {}

  const supported = navigator.mediaDevices?.getSupportedConstraints?.() || {};
  const preferred = {};
  if (supported.echoCancellation) preferred.echoCancellation = true;
  if (supported.noiseSuppression) preferred.noiseSuppression = true;
  if (supported.autoGainControl) preferred.autoGainControl = true;
  if (supported.channelCount) preferred.channelCount = { ideal: 1, max: 1 };

  if (Object.keys(preferred).length && track.applyConstraints) {
    await track.applyConstraints(preferred).catch(() => undefined);
  }
  return track;
};

export const prepareWorshipTrack = async (track) => {
  if (!track) return track;
  try { track.contentHint = 'music'; } catch {}

  const supported =
    (typeof navigator !== 'undefined' &&
      navigator.mediaDevices?.getSupportedConstraints?.()) || {};
  const preferred = {};
  if (supported.echoCancellation) preferred.echoCancellation = false;
  if (supported.noiseSuppression) preferred.noiseSuppression = false;
  if (supported.autoGainControl) preferred.autoGainControl = false;
  if (supported.channelCount) preferred.channelCount = { ideal: 2 };
  if (supported.sampleRate) preferred.sampleRate = { ideal: 48000 };
  if (supported.latency) preferred.latency = { ideal: 0.01 };

  if (Object.keys(preferred).length && track.applyConstraints) {
    await track.applyConstraints(preferred).catch(() => undefined);
  }
  return track;
};

export const prepareMeetingAudioTrack = (
  track,
  mode = MEETING_AUDIO_MODES.VOICE
) =>
  normalizeMeetingAudioMode(mode) === MEETING_AUDIO_MODES.WORSHIP
    ? prepareWorshipTrack(track)
    : prepareVoiceTrack(track);

export const getMeetingAudioProducerAppData = (
  mode = MEETING_AUDIO_MODES.VOICE,
  { processed = false } = {}
) => {
  const normalized = normalizeMeetingAudioMode(mode);
  return normalized === MEETING_AUDIO_MODES.WORSHIP
    ? {
        processed: Boolean(processed),
        audioMode: normalized,
        contentType: 'music',
        voiceFocused: false,
      }
    : {
        processed: Boolean(processed),
        audioMode: normalized,
        contentType: 'speech',
        voiceFocused: true,
      };
};

export const getMeetingOpusCodecOptions = (
  mode = MEETING_AUDIO_MODES.VOICE
) =>
  normalizeMeetingAudioMode(mode) === MEETING_AUDIO_MODES.WORSHIP
    ? {
        opusStereo: 1,
        opusFec: 1,
        opusDtx: 0,
        opusMaxAverageBitrate: 128000,
        opusMaxPlaybackRate: 48000,
        opusPtime: 10,
      }
    : {
        opusStereo: 0,
        opusFec: 1,
        opusDtx: 1,
        opusMaxAverageBitrate: 40000,
        opusMaxPlaybackRate: 48000,
        opusPtime: 20,
      };
